import React, { useState, useRef, useEffect } from 'react';

interface SavePromptDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (name: string, tags?: string[]) => void;
  promptContent: string;
}

const SavePromptDialog: React.FC<SavePromptDialogProps> = ({
  open,
  onClose,
  onConfirm,
  promptContent
}) => {
  const [promptName, setPromptName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Small delay to ensure the dialog is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setPromptName('');
      setTags([]);
      setTagInput('');
      setIsSubmitting(false);
    }
  }, [open]);

  // Tag handling functions
  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.includes(',')) {
      // Split by comma and add each tag
      const newTags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
      newTags.forEach(tag => {
        if (!tags.includes(tag)) {
          setTags(prev => [...prev, tag]);
        }
      });
      setTagInput('');
    } else {
      setTagInput(value);
    }
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (tagInput.trim()) {
        addTag(tagInput);
      }
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      // Remove last tag when backspacing on empty input
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleTagInputBlur = () => {
    // Convert any remaining text to a tag when input loses focus
    if (tagInput.trim()) {
      addTag(tagInput);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = promptName.trim();
    
    if (!trimmedName) {
      return;
    }
    
    // Convert any remaining text in tag input to a tag before saving
    let finalTags = [...tags];
    if (tagInput.trim()) {
      const trimmedTag = tagInput.trim();
      if (!finalTags.includes(trimmedTag)) {
        finalTags.push(trimmedTag);
      }
    }
    
    setIsSubmitting(true);
    try {
      // Update the onConfirm call to pass the final tags
      await onConfirm(trimmedName, finalTags);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error saving prompt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isSubmitting) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={handleCancel}
      onKeyDown={handleKeyDown}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          minWidth: '400px',
          maxWidth: '90vw',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dialog Header */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '8px'
          }}>
            Save to PromptVault
          </h2>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#666',
            lineHeight: '1.4'
          }}>
            Enter a name for this prompt to save it to your PromptVault for future use.
          </p>
        </div>

        {/* Preview of prompt content */}
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '20px',
          maxHeight: '100px',
          overflowY: 'auto'
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: 500,
            color: '#666',
            marginBottom: '6px'
          }}>
            Prompt to save:
          </div>
          <div style={{
            fontSize: '14px',
            color: '#333',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {promptContent.length > 200 
              ? `${promptContent.substring(0, 200)}...` 
              : promptContent
            }
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              marginBottom: '6px'
            }}>
              Prompt Name *
            </label>
            <input
              ref={inputRef}
              type="text"
              value={promptName}
              onChange={(e) => setPromptName(e.target.value)}
              placeholder="Enter a descriptive name..."
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-color, #007bff)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
            />
          </div>

          {/* Tags Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              marginBottom: '6px'
            }}>
              Tags (optional)
            </label>
            
            {/* Tags display and input container */}
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '8px',
              minHeight: '42px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: isSubmitting ? '#f8f9fa' : 'white',
              transition: 'border-color 0.2s',
              cursor: isSubmitting ? 'not-allowed' : 'text'
            }}
            onClick={() => !isSubmitting && tagInputRef.current?.focus()}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary-color, #007bff)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#ddd';
            }}
            >
              {/* Existing tags as chips */}
              {tags.map((tag, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#e7f3ff',
                    color: '#0066cc',
                    padding: '4px 8px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    gap: '4px'
                  }}
                >
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTag(tag);
                    }}
                    disabled={isSubmitting}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0066cc',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      padding: '0',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      lineHeight: 1,
                      opacity: isSubmitting ? 0.5 : 1
                    }}
                    title="Remove tag"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              {/* Tag input field */}
              <input
                ref={tagInputRef}
                type="text"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                onBlur={handleTagInputBlur}
                placeholder={tags.length === 0 ? "Add tags separated by commas..." : ""}
                disabled={isSubmitting}
                style={{
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                  fontSize: '14px',
                  flex: 1,
                  minWidth: '120px',
                  padding: '2px 4px'
                }}
              />
            </div>
            
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              Press comma or enter to add a tag. Use backspace to remove tags.
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isSubmitting ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  (e.target as HTMLElement).style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  (e.target as HTMLElement).style.backgroundColor = 'white';
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!promptName.trim() || isSubmitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: (!promptName.trim() || isSubmitting) 
                  ? '#ccc' 
                  : 'var(--primary-color, #007bff)',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                cursor: (!promptName.trim() || isSubmitting) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                minWidth: '80px'
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SavePromptDialog; 